import React from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box, Flex } from '@atlaskit/primitives/compiled';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { StatusLozengeButton } from '../../../../../../../common/ui/status-lozenge-button';
import { enrichExcerpt } from '../../../../../../../common/utils/search-result-utils';

import type { searchGraphProject_AtlassianSearchPage$key } from './__generated__/searchGraphProject_AtlassianSearchPage.graphql';
import { messages } from './messages';
import { formatProjectState, getProjectStateAppearance } from './transforms';

const styles = cssMap({
	projectContainer: {
		width: '100%',
	},
	resultWrapper: {
		flex: '1',
		minWidth: '0px',
	},
});

/**
 * Renders a third-party "Project" search result (e.g. a ServiceNow project).
 *
 * In addition to the standard title/description/header metadata, this renderer
 * surfaces a **state** lozenge sourced from the {@code ExternalProject} entity's
 * {@code status} field — capitalised and truncated when long (via
 * {@link StatusLozengeButton}) and colour-coded based on the freeform value.
 *
 * The state value is a plain freeform string on the backend, so it is
 * transformed before rendering (see {@link formatProjectState}).
 */
export const SearchGraphProject = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	mainContentWidth,
	selectedSites,
}: SearchGraphResultProps<searchGraphProject_AtlassianSearchPage$key>): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment searchGraphProject_AtlassianSearchPage on SearchResultGraphDocument
			@argumentDefinitions(
				isProjectRendererEnabled: {
					type: "Boolean!"
					provider: "is-project-renderer-enabled.relayprovider"
				}
			) {
				id
				url
				title
				lastModifiedDate
				excerpt
				entity @include(if: $isProjectRendererEnabled) {
					... on ExternalProject {
						status
					}
				}
			}
		`,
		node,
	);

	const { id, url, title, lastModifiedDate, excerpt, entity } = data;

	const description = <DescriptionBody description={enrichExcerpt(excerpt)} />;

	const lastModifiedComponent = <LastModified lastModified={lastModifiedDate} />;

	// `state` is a freeform string -> capitalise the first letter (truncation is
	// handled by StatusLozengeButton).
	const stateText = formatProjectState(entity?.status);
	// Colour-code the lozenge based on the freeform state (mirrors the way
	// SearchResultAtlasProject colours its status lozenge) instead of grey.
	const stateAppearance = getProjectStateAppearance(entity?.status);

	const headerMetadataParts = [
		formatMessage(messages.projectLabel),
		['last-modified', lastModifiedComponent],
	];

	return (
		<Flex gap="space.100" alignItems="start" role="listitem" xcss={styles.projectContainer}>
			<Box xcss={styles.resultWrapper}>
				<SearchResult
					id={id}
					product={product}
					productSelected={!!selectedProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultContextMenu}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					icon={icon}
					description={description}
					headerMetadataParts={headerMetadataParts}
					title={enrichExcerpt(title)}
					role="none"
					url={url}
					isThirdParty
					cloudId={cloudId}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					position={position}
					partial={data}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			</Box>
			{stateText && <StatusLozengeButton statusText={stateText} appearance={stateAppearance} />}
		</Flex>
	);
};
